import { Table, TableBody } from "@/components/ui/table";
import { BODY_OUTER } from "@/v2/layout/AdminLayout";
import {
    useReactTable,
    getCoreRowModel,
    ColumnDef,
    Row,
    RowSelectionState,
} from "@tanstack/react-table";
import { TablePagination } from "./Pagination";
import DefaultTableRow from "../row/TableRow";
import { ReactNode, useState } from "react";
import { Pagination } from "@/v2/types/Datatable";
import TableRowSkeleton from "../row/Skeleton";
import { DataTableHeader } from "./TableHeader";

interface DataTableProps<T = unknown> {
    data: T[];
    columns: ColumnDef<T, any>[];
    pagination?: Pagination;
    specialRow?: (row: Row<T>, i: number) => ReactNode;
    loading?: boolean;
    reduceSizeBy?: number;
    showSelect?: boolean;
}

export function DataTable<Tdata>({
    data,
    columns,
    specialRow,
    pagination,
    loading,
    reduceSizeBy = 0,
    showSelect = false,
}: DataTableProps<Tdata>) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onRowSelectionChange: setRowSelection,
        state: {
            rowSelection,
            columnVisibility: { select: showSelect },
        },
    });

    return (
        <>
            <div
                className="relative overflow-y-scroll rounded-xl border border-gray-200 shadow-sm"
                style={{
                    height: `calc(100vh - ${BODY_OUTER + 240 + reduceSizeBy}px)`,
                }}
            >
                <Table className="w-full text-left text-sm">
                    <DataTableHeader<Tdata> table={table} />
                    {loading ? (
                        <TableBody>
                            {Array.from({ length: 20 }).map((_, i) => (
                                <TableRowSkeleton
                                    key={i}
                                    columnsLength={
                                        table.getHeaderGroups()[0].headers
                                            .length
                                    }
                                />
                            ))}
                        </TableBody>
                    ) : !data ? (
                        <p> error</p>
                    ) : (
                        <TableBody>
                            {table.getRowModel().rows.map((row, i) => {
                                return specialRow ? (
                                    specialRow(row, i)
                                ) : (
                                    <DefaultTableRow
                                        key={row.id}
                                        row={row}
                                        i={i}
                                    />
                                );
                            })}
                        </TableBody>
                    )}
                </Table>
            </div>
            {pagination && <TablePagination pagination={pagination!} />}
        </>
    );
}
